import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "aicaller_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const api = axios.create({ baseURL: API });
api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
// Only the signed-in tools live under /app (and the admin pages under /fortune/*). A stale or expired token on a PUBLIC page — the
// homepage, /aog-coin, /fortune, a campaign landing — must never bounce a prospect to the login screen (Aziz, Oct 14: a store owner
// clicking the email link landed on "sign up"). Public pages just drop the dead token and keep rendering.
const PROTECTED_PREFIXES = ["/app", "/fortune/inquiries", "/fortune/map", "/fortune/broadcast", "/fortune/flyer", "/fortune/social", "/portal", "/account"];
export const isProtectedPath = (pathname) => PROTECTED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`) || pathname.startsWith(`${p}?`));

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status === 403 && typeof detail === "string" && detail.startsWith("Read-only admin")) {
      import("sonner").then(({ toast }) => toast.error(detail, { id: "read-only-admin" }));
    }
    if (err?.response?.status === 401) {
      clearToken();
      const path = window.location.pathname;
      if (path !== "/login" && isProtectedPath(path)) window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export async function streamCall(leadId, onDelta, onDone, onError) {
  const token = getToken();
  const res = await fetch(`${API}/ai/call/${leadId}/stream`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok || !res.body) {
    onError?.(new Error(`Call failed (${res.status})`));
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop() || "";
    for (const p of parts) {
      const line = p.split("\n").find((l) => l.startsWith("data:"));
      if (!line) continue;
      try {
        const obj = JSON.parse(line.slice(5).trim());
        if (obj.type === "delta") onDelta?.(obj.text);
        else if (obj.type === "done") onDone?.(obj);
        else if (obj.type === "error") onError?.(new Error(obj.message));
      } catch (e) { /* ignore */ }
    }
  }
}

export async function getCallConfig() {
  const { data } = await api.get("/ai/call/config");
  return data;
}

export async function dialRealCall(leadId, toOverride) {
  const { data } = await api.post(`/ai/call/${leadId}/dial`, { to_override: toOverride || null });
  return data;
}

export async function getCallSession(sessionId) {
  const { data } = await api.get(`/ai/call/session/${sessionId}`);
  return data;
}

export async function importFromPlaces(payload) {
  const { data } = await api.post("/places/import", payload);
  return data;
}

export async function streamBatchEnrich(payload, onProgress, onDone, onError) {
  const token = getToken();
  const res = await fetch(`${API}/ai/enrich-batch/stream`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok || !res.body) { onError?.(new Error(`Batch failed (${res.status})`)); return; }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop() || "";
    for (const p of parts) {
      const line = p.split("\n").find((l) => l.startsWith("data:"));
      if (!line) continue;
      try {
        const obj = JSON.parse(line.slice(5).trim());
        if (obj.type === "progress") onProgress?.(obj);
        else if (obj.type === "start") onProgress?.(obj);
        else if (obj.type === "done") { onDone?.(obj); return; }
      } catch { /* ignore */ }
    }
  }
}

export async function draftFollowup(leadId) {
  const { data } = await api.post("/ai/followup-email", { lead_id: leadId });
  return data;
}

export async function sendEmail(payload) {
  const { data } = await api.post("/email/send", payload);
  return data;
}
